import { groupBy, max, orderBy } from "lodash";
import { useLayoutEffect, useState } from "react";
import type { useQueryReferencesValuesResult } from "src/api/marketing-actions";
import type { TFunction } from "src/design-system/core/TranslationContext";
import { AutoRulesComparePeriodEnum } from "src/types/enum/AutoRulesComparePeriodEnum";
import { ALL_DAYS_OF_THE_WEEK, DayOfTheWeek } from "src/types/enum/DayOfTheWeek";
import { ServiceTime } from "src/types/offerSlot";
import type { Pos } from "src/types/pos";
import { TriggerMetricEnum } from "src/types/trigger";
import { INFINITE_TAG } from "src/utils/trigger";
import {
    MAX_EDGE_VALUE,
    MIN_EDGE_VALUE,
} from "src/views/marketing-actions/components/form/ScenarioCard/AddScenarioDialog";
import type { CustomScenario } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioCard";

export const formatPercentValueForScenarios = (value: number): string => {
    if (value === undefined || value === null || value === MAX_EDGE_VALUE) {
        return `+${INFINITE_TAG}%`;
    }
    if (value === 0) {
        return `${value.toString()}%`;
    }
    if (value > 0) {
        return `${value.toString()}%`;
    }
    return `${value.toString()}%`;
};
export interface FormattedReferenceValue {
    pos?: string;
    posId: number;
    metric: TriggerMetricEnum;
    maximum: number;
    isEditedDowNumberListLunch: number[];
    isEditedDowNumberListDinner: number[];
    thresholdFactor?: number;
    // List of objectives for each pair dow/serviceTime in the form of {dow}_{serviceTime}
    [key: string]: number | string | number[] | undefined;
}

const valueMapperMetrics: { [key in TriggerMetricEnum]: string } = {
    [TriggerMetricEnum.DailyProfits]: "sumProfit",
    [TriggerMetricEnum.DailySales]: "sumInclVat",
    [TriggerMetricEnum.DailySalesExvat]: "sumExVat",
    [TriggerMetricEnum.DailyOrders]: "orders",
};

export const formatReferencesValues = (
    triggerReferencesValues: useQueryReferencesValuesResult[],
    metric: TriggerMetricEnum,
    posList: Pos[]
): FormattedReferenceValue[] => {
    const groupedByPosId = groupBy(triggerReferencesValues, "posId");

    const data = Object.entries(groupedByPosId).map(([key, values]) => {
        const neededValue = valueMapperMetrics[metric] ?? "orders";

        const dayValues = ALL_DAYS_OF_THE_WEEK.reduce(
            (acc, day) =>
                [ServiceTime.Lunch, ServiceTime.Dinner].reduce(
                    (acc2, serviceTime) => ({
                        ...acc2,
                        [`${day}_${serviceTime}`]:
                            values.find(
                                (item) => item.orderDow === day && item.serviceTime === serviceTime
                            )?.[neededValue] ?? 0,
                    }),
                    acc
                ),
            {}
        );

        return {
            posId: parseInt(key, 10),
            pos: posList?.find((e) => e.id === parseInt(key, 10))?.name,
            maximum: max(values.map((item) => item?.[neededValue] ?? 0)),
            metric,
            isEditedDowNumberListLunch: [],
            isEditedDowNumberListDinner: [],
            ...dayValues,
        };
    });

    // push empty data in case of pos without data
    posList.forEach((pos) => {
        const tmp = data.find((item) => item.posId === pos.id);
        if (!tmp) {
            data.push({
                posId: pos.id,
                pos: pos.name,
                metric,
                maximum: 0,
                isEditedDowNumberListLunch: [],
                isEditedDowNumberListDinner: [],
                ...ALL_DAYS_OF_THE_WEEK.reduce(
                    (acc, day) =>
                        [ServiceTime.Lunch, ServiceTime.Dinner].reduce(
                            (acc2, serviceTime) => ({
                                ...acc2,
                                [`${day}_${serviceTime}`]: 0,
                            }),
                            acc
                        ),
                    {}
                ),
            });
        }
    });

    return orderBy(data, "pos");
};

const checkExistValueForDayAndService = (item: FormattedReferenceValue, day: DayOfTheWeek) => {
    if (item[`${day}_${ServiceTime.Dinner}`] || item[`${day}_${ServiceTime.Lunch}`]) {
        return true;
    }
    return false;
};
export const generateBlackListColumns = (
    formattedReferenceValueList: FormattedReferenceValue[]
): string[] => {
    const blackList = [];
    // Check if all values by days are empty, if its empty black list column

    const days = [
        { day: DayOfTheWeek.MONDAY, number: 0 },
        { day: DayOfTheWeek.TUESDAY, number: 0 },
        { day: DayOfTheWeek.WEDNESDAY, number: 0 },
        { day: DayOfTheWeek.THURSDAY, number: 0 },
        { day: DayOfTheWeek.FRIDAY, number: 0 },
        { day: DayOfTheWeek.SATURDAY, number: 0 },
        { day: DayOfTheWeek.SUNDAY, number: 0 },
    ];

    days.forEach((d) => {
        let display = false;
        formattedReferenceValueList.forEach((item) => {
            if (checkExistValueForDayAndService(item, d.day)) {
                display = true;
            }
        });
        if (display === false) {
            blackList.push(`${d.day}_${ServiceTime.Lunch}`, `${d.day}_${ServiceTime.Dinner}`);
        }
    });

    return blackList;
};

export const generateEdges = (): { min: number; max: number } => {
    return {
        min: MIN_EDGE_VALUE,
        max: MAX_EDGE_VALUE,
    };
};

export const computeMinMaxEdgesForDefaultValueSelect = (
    idNewScenario: number,
    scenarios: CustomScenario[],
    isUpdate: boolean
): { min: number; max: number } => {
    // UPDATE AND ONE SCENARIO
    if (isUpdate === true && scenarios?.length === 1) {
        return {
            min: MIN_EDGE_VALUE,
            max: MAX_EDGE_VALUE,
        };
    }

    // NO MIN EDGES
    if (idNewScenario === 0 && scenarios?.length >= 1 && isUpdate === true) {
        return {
            min: MIN_EDGE_VALUE,
            max: scenarios[0].triggerMax,
        };
    }

    if (idNewScenario === 0 && scenarios?.length >= 1 && isUpdate === false) {
        return {
            min: MIN_EDGE_VALUE,
            max: scenarios[0].triggerMin,
        };
    }

    // NO MAX EDGES
    if (
        scenarios?.length > 0 &&
        (scenarios?.length === idNewScenario ||
            (isUpdate && scenarios?.length === idNewScenario + 1))
    ) {
        return {
            min: scenarios[idNewScenario - 1].triggerMax,
            max: MAX_EDGE_VALUE,
        };
    }

    // EDGES BETWEEN TWO SCENARIOS
    if (scenarios?.length > 0 && idNewScenario > 0 && idNewScenario < scenarios?.length) {
        return {
            min: scenarios[idNewScenario - 1].triggerMax,
            max: scenarios[idNewScenario].triggerMin,
        };
    }

    return {
        min: MIN_EDGE_VALUE,
        max: MAX_EDGE_VALUE,
    };
};

export const useIsOverflow = (ref: React.RefObject<HTMLElement>): boolean => {
    const [isOverflow, setIsOverflow] = useState(undefined);

    useLayoutEffect(() => {
        const { current } = ref;
        const trigger = () => {
            const hasOverflow = current.scrollWidth > current.clientWidth;
            setIsOverflow(hasOverflow);
        };

        if (current) {
            if ("ResizeObserver" in window) {
                new ResizeObserver(trigger).observe(current);
            }
            trigger();
        }
    }, [ref]);

    return isOverflow;
};

export const formatScenarioSubmitExplanation = (
    scenarios: CustomScenario[],
    t: TFunction,
    translateCommon: TFunction
): string => {
    if (scenarios === undefined || scenarios === null || scenarios.length === 0) {
        return "";
    }

    const length = scenarios.length;
    const minValueTrigger = scenarios[0].triggerMin;
    const maxValueTrigger = scenarios[length - 1].triggerMax;

    return t("columns.scenario-explanation", {
        max: formatPercentValueForScenarios(maxValueTrigger),
        min: formatPercentValueForScenarios(minValueTrigger),
        numberOfScenario: length.toString() ?? "",
        isPluralScenario:
            length > 1
                ? translateCommon("concepts.scenarios")
                : translateCommon("concepts.scenario"),
    });
};

export const getCorrectLinkWord = (
    translateCommon: TFunction,
    comparePeriod: AutoRulesComparePeriodEnum,
    variation = false
): string => {
    let linkWord = variation
        ? translateCommon("word-link.the-s")
        : translateCommon("word-link.of-s");

    if (comparePeriod === AutoRulesComparePeriodEnum.lastWeek) {
        linkWord = variation
            ? translateCommon("word-link.the-f")
            : translateCommon("word-link.of-this");
    }
    if (
        comparePeriod === AutoRulesComparePeriodEnum.weekLastYear ||
        comparePeriod === AutoRulesComparePeriodEnum.monthLastYear
    ) {
        linkWord = variation
            ? translateCommon("word-link.the-a")
            : translateCommon("word-link.of-the");
    }
    return linkWord;
};
